import React from 'react';
import { Helmet } from 'react-helmet-async';
import { useLocation } from 'react-router-dom';

interface SEOOptimizedProps {
  title?: string;
  description?: string;
  keywords?: string;
  image?: string;
  url?: string;
  /** Extra page-specific JSON-LD (Article, FAQPage, BreadcrumbList…). One object or an array. */
  jsonLd?: Record<string, unknown> | Record<string, unknown>[];
  /** Keep the page out of Google (the reference catalog's product pages, 2026-10-10). */
  noindex?: boolean;
}

const SEOOptimized: React.FC<SEOOptimizedProps> = ({
  title = "בנה מחשבים - מעבדת מחשבים בנטור, רמת הגולן",
  description = "מעבדת מחשבים בנטור, רמת הגולן: תיקון ושדרוג מחשבים ניידים ונייחים. ייעוץ חינם, מחיר בכתב לפני שמתחילים, 3 חודשי אחריות. 054-667-2863",
  keywords = "תיקון מחשבים רמת הגולן, מחשבים קצרין, טכנאי מחשבים נטור, בניית מחשב גיימינג, שדרוג מחשב גולן, תיקון לפטופ צפון, מעבדת מחשבים רמת הגולן, מחשבים טבריה, בנה מחשבים, שדרוג SSD, תמיכה מרחוק",
  image = "https://benapc.com/og-image.jpg",
  url,
  jsonLd,
  noindex = false,
}) => {
  const { pathname } = useLocation();
  // Per-page canonical/OG url: explicit prop wins; otherwise derive from the
  // current route so inner pages don't all canonical to the homepage.
  const resolvedUrl = url ?? `https://benapc.com${pathname === '/' ? '' : pathname}`;
  // The one business entity (2026-10-14). The homepage and index.html used to
  // emit two more, with another phone, name and address; the listing kit needs
  // one identical name/address/phone everywhere. Business facts only — the
  // page's own description goes in the meta tags, not here.
  const structuredData = {
    "@context": "https://schema.org",
    "@type": "ComputerStore",
    "@id": "https://benapc.com/#business",
    // Exactly the Google Business Profile name (owner 2026-10-14).
    "name": "BENA-בנה מחשבים",
    "alternateName": ["בנה מחשבים - BenaPC", "BenaPC", "בנה מחשבים"],
    "description": "מעבדת מחשבים בנטור, רמת הגולן: תיקון ושדרוג מחשבים ניידים ונייחים. ייעוץ חינם, מחיר בכתב לפני שמתחילים, 3 חודשי אחריות.",
    "url": "https://benapc.com",
    "image": "https://benapc.com/icon-512x512.png",
    "logo": "https://benapc.com/icon-512x512.png",
    "telephone": "+972-54-667-2863",
    "email": "info@benapc.com",
    "areaServed": ["רמת הגולן", "הגליל העליון", "עמק החולה", "טבריה", "קצרין", "חד-נס", "אלוני הבשן", "מג'דל שמס", "צפת", "ראש פינה", "חצור הגלילית", "קריית שמונה"],
    "priceRange": "₪₪",
    "address": {
      "@type": "PostalAddress",
      "streetAddress": "המייסדים 1",
      "postalCode": "1291500",
      "addressLocality": "נטור",
      "addressRegion": "רמת הגולן",
      "addressCountry": "IL"
    },
    "geo": {
      "@type": "GeoCoordinates",
      "latitude": 32.8538,
      "longitude": 35.7515
    },
    "foundingDate": "2018",
    "founder": { "@type": "Person", "name": "בניהו משה" },
    "vatID": "201536679",
    "sameAs": [
      "https://www.facebook.com/bena.computer/",
      "https://www.instagram.com/bena.computers/"
    ],
    "potentialAction": {
      "@type": "ReserveAction",
      "target": {
        "@type": "EntryPoint",
        "urlTemplate": "https://benapc.com/booking",
        "actionPlatform": ["https://schema.org/DesktopWebPlatform", "https://schema.org/MobileWebPlatform"]
      },
      "result": { "@type": "Reservation", "name": "קביעת תור למעבדה" }
    },
    "hasOfferCatalog": {
      "@type": "OfferCatalog",
      "name": "שירותי מחשוב",
      "itemListElement": [
        {
          "@type": "Offer",
          "itemOffered": {
            "@type": "Service",
            "name": "תיקון מחשבים",
            "description": "תיקון מחשבים נייחים וניידים, אבחון ופתרון תקלות"
          }
        },
        {
          "@type": "Offer",
          "itemOffered": {
            "@type": "Service",
            "name": "בניית מחשבי גיימינג",
            "description": "בניית מחשבי גיימינג ועבודה בהתאמה אישית, עם בדיקות עומס לפני המסירה"
          }
        },
        {
          "@type": "Offer",
          "itemOffered": {
            "@type": "Service",
            "name": "שדרוג מחשבים",
            "description": "שדרוג SSD, RAM, כרטיס מסך ועוד"
          }
        },
        {
          "@type": "Offer",
          "itemOffered": {
            "@type": "Service",
            "name": "תמיכה מרחוק",
            "description": "תמיכה טכנית מרחוק לפתרון בעיות"
          }
        },
        {
          "@type": "Offer",
          "itemOffered": {
            "@type": "Service",
            "name": "שחזור נתונים",
            "description": "שחזור נתונים מכוננים קשיחים ו-SSD"
          }
        }
      ]
    }
  };

  return (
    <Helmet>
      {/* Primary Meta Tags */}
      <title>{title}</title>
      <meta name="description" content={description} />
      <meta name="keywords" content={keywords} />
      <meta name="viewport" content="width=device-width, initial-scale=1.0" />
      <meta name="robots" content={noindex ? 'noindex, follow' : 'index, follow'} />
      <meta name="language" content="Hebrew" />
      <meta name="author" content="בנה מחשבים" />
      <meta name="geo.region" content="IL-Z" />
      <meta name="geo.placename" content="Natur, Golan Heights" />

      {/* Open Graph / Facebook */}
      <meta property="og:type" content="website" />
      <meta property="og:url" content={resolvedUrl} />
      <meta property="og:title" content={title} />
      <meta property="og:description" content={description} />
      <meta property="og:image" content={image} />
      <meta property="og:site_name" content="בנה מחשבים - BenaPC" />
      <meta property="og:locale" content="he_IL" />

      {/* Twitter */}
      <meta property="twitter:card" content="summary_large_image" />
      <meta property="twitter:url" content={resolvedUrl} />
      <meta property="twitter:title" content={title} />
      <meta property="twitter:description" content={description} />
      <meta property="twitter:image" content={image} />

      {/* Canonical URL */}
      <link rel="canonical" href={resolvedUrl} />

      {/* Favicon and Icons */}
      <link rel="icon" type="image/x-icon" href="/favicon.ico" />

      {/* Preconnect to external domains */}
      <link rel="preconnect" href="https://fonts.googleapis.com" />
      <link rel="preconnect" href="https://fonts.gstatic.com" crossOrigin="anonymous" />

      {/* Structured Data — site-wide business entity */}
      <script type="application/ld+json">
        {JSON.stringify(structuredData)}
      </script>

      {/* Page-specific structured data (Article / FAQPage / Product / Breadcrumb) */}
      {jsonLd && (Array.isArray(jsonLd) ? jsonLd : [jsonLd]).map((obj, i) => (
        <script key={`ld-${i}`} type="application/ld+json">
          {JSON.stringify(obj)}
        </script>
      ))}

      {/* Performance hints */}
      <meta httpEquiv="x-dns-prefetch-control" content="on" />
      <link rel="dns-prefetch" href="//wa.me" />

      {/* Security headers */}
      <meta httpEquiv="X-Content-Type-Options" content="nosniff" />
      <meta httpEquiv="Referrer-Policy" content="strict-origin-when-cross-origin" />
    </Helmet>
  );
};

export default SEOOptimized;
